import { ThreadDoc, type Address } from "@kwp/contracts";
import { reviveTimestamps } from "@kwp/firestore";

/**
 * A cached thread as the inbox shows it (architecture §6.4, §8): the stored document plus its
 * scoped id `{accountId}:{providerThreadId}`, the id every mail api call uses.
 */
/**
 * `unclassified`: the document has no `kind` yet (cached before WP-061, until the raw fill reaches
 * it); the contract reads it as a person's, but All mail shows it with no kind tag (WP-064).
 */
export type Thread = ThreadDoc & { id: string; providerId: string; unclassified?: true };

/** Reads one `accounts/{a}/threads/{t}` document; one that does not match the contract is skipped. */
export function toThread(providerId: string, data: unknown): Thread | null {
  const parsed = ThreadDoc.safeParse(reviveTimestamps(data));
  if (!parsed.success) return null;
  const unclassified = typeof data !== "object" || data === null || !("kind" in data);
  return { ...parsed.data, id: `${parsed.data.accountId}:${providerId}`, providerId, ...(unclassified ? { unclassified: true as const } : {}) };
}

/** Pages merged by id (an earlier page holds the fresher copy), newest first. */
export function mergePages(pages: ReadonlyArray<readonly Thread[]>): Thread[] {
  const byId = new Map<string, Thread>();
  for (const page of pages) for (const t of page) if (!byId.has(t.id)) byId.set(t.id, t);
  return [...byId.values()].sort((a, b) => b.lastMessageAt.getTime() - a.lastMessageAt.getTime() || a.id.localeCompare(b.id));
}

/** The thread's message ids as the mail actions take them (scoped; Gmail labels are per message, §6.2). */
export function scopedMessageIds(thread: Pick<Thread, "accountId" | "messageIds">): string[] {
  return thread.messageIds.map((m) => `${thread.accountId}:${m}`);
}

const firstName = (a: Address) => {
  if (a.name === null || a.name.trim() === "") return a.email.split("@")[0] as string;
  return a.name.trim().split(/\s+/)[0] as string;
};

/**
 * Who a thread is with: participants other than the mailbox itself, first names, at most three
 * (then "+n"). A thread only with yourself reads "me".
 */
export function senders(participants: readonly Address[], ownAddress: string | null): string {
  const own = ownAddress === null ? null : ownAddress.toLowerCase();
  const others = participants.filter((p) => p.email.toLowerCase() !== own);
  if (others.length === 0) return "me";
  const names = others.slice(0, 3).map(firstName);
  return others.length > 3 ? `${names.join(", ")} +${others.length - 3}` : names.join(", ");
}

/** The address of the first participant other than the mailbox itself, shown beside the names; null when there is none. */
export function senderAddress(participants: readonly Address[], ownAddress: string | null): string | null {
  const own = ownAddress === null ? null : ownAddress.toLowerCase();
  const other = participants.find((p) => p.email.toLowerCase() !== own);
  return other === undefined ? null : other.email;
}

const TIME = new Intl.DateTimeFormat("en", { hour: "numeric", minute: "2-digit" });
const DAY = new Intl.DateTimeFormat("en", { month: "short", day: "numeric" });
const FULL = new Intl.DateTimeFormat("en", { year: "numeric", month: "short", day: "numeric" });

/** Today: the time; this year: "Oct 9"; older: "Oct 9, 2025". */
export function relativeDate(date: Date, now: Date): string {
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) return TIME.format(date);
  if (date.getFullYear() === now.getFullYear()) return DAY.format(date);
  return FULL.format(date);
}

/** Human file size: "980 B", "12 KB", "3.4 MB". */
export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * What the tag and move pickers suggest for some accounts: their known tags (`accounts/{a}/tags`)
 * plus tags already on loaded threads. Moving offers folders, and on Gmail labels too (a Gmail
 * "folder" is a label). Case-insensitively unique, sorted.
 */
export function pickerSuggestions(
  picker: "tag" | "move",
  known: ReadonlyArray<{ accountId: string; name: string; kind: "tag" | "folder" }>,
  accounts: ReadonlyArray<{ id: string; provider: "gmail" | "graph" }>,
  seen: readonly string[],
): string[] {
  const gmail = new Set(accounts.filter((a) => a.provider === "gmail").map((a) => a.id));
  const ids = new Set(accounts.map((a) => a.id));
  const names = known
    .filter((t) => ids.has(t.accountId))
    .filter((t) => (picker === "tag" ? t.kind === "tag" : t.kind === "folder" || gmail.has(t.accountId)))
    .map((t) => t.name);
  const out = new Map<string, string>();
  for (const name of [...names, ...(picker === "tag" || gmail.size > 0 ? seen : [])]) if (!out.has(name.toLowerCase())) out.set(name.toLowerCase(), name);
  return [...out.values()].sort((a, b) => a.localeCompare(b));
}

/** Distinguishable chip colours, readable in light and dark; an account keeps its colour. */
export const PALETTE = ["#3554c8", "#0f7b6c", "#b5470f", "#8a3fb4", "#a1306a", "#4d7a12", "#1f6f9f", "#8c6a00"];

/** The mailbox's colour: the one chosen in Settings → Accounts, else one picked from its id. */
export function accountColour(accountId: string, chosen: string | null = null): string {
  if (chosen !== null) return chosen;
  let h = 0;
  for (const ch of accountId) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return PALETTE[h % PALETTE.length] as string;
}

/**
 * What a mutation does to a thread before the listener confirms it (optimistic UI). `hidden`
 * removes it from the current list (archive, trash, spam, move).
 */
export interface ThreadPatch {
  hidden?: boolean;
  unread?: boolean;
  starred?: boolean;
  tags?: string[];
}

export function applyPatches(threads: readonly Thread[], patches: ReadonlyMap<string, ThreadPatch>): Thread[] {
  if (patches.size === 0) return [...threads];
  return threads.flatMap((t) => {
    const p = patches.get(t.id);
    if (p === undefined) return [t];
    if (p.hidden === true) return [];
    return [
      {
        ...t,
        ...(p.unread === undefined ? {} : { unread: p.unread }),
        ...(p.starred === undefined ? {} : { starred: p.starred }),
        ...(p.tags === undefined ? {} : { tags: p.tags }),
      },
    ];
  });
}
